import { ApiResponseType, jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  appMutationResponseSchema,
  appRequestsPageSchema,
  appRequestSchema,
  createdPublisherAppSchema,
  collectedAppDataResponseSchema,
  emptyMutationResponseSchema,
  publisherAppsImportResponseSchema,
  SupplyAppsSortDirection,
  serializeAppRequestsListFilter,
  serializeSupplyAppsListFilter,
  supplyAppsReferenceListSchema,
  supplyAppsStatusCountsSchema,
  supplyInventoryAppEditSchema,
  supplyInventoryAppsPageSchema,
  type AppRequest,
  type AppRequestsListFilter,
  type CreateAppFromRequestPayload,
  type PublisherAppImportPayload,
  type PublisherAppValidationPayload,
  type SupplyAppMutationPayload,
  type SupplyAppsListFilter,
} from './model';
import {
  serializeSupplyAppStatusCountsFilter,
  type SupplyAppStatusCountsFilter,
} from './statusCounts';

interface SupplyAppsClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const importResponseSchema = z.preprocess(
  (value) => (value === '' ? null : value),
  z.union([z.null(), z.string(), z.looseObject({}), z.array(z.looseObject({}))]),
);

const publisherLookupPageSchema = z.looseObject({
  content: z.array(z.looseObject({ id: z.string(), name: z.string() })),
  totalElements: z.number(),
});

export function createSupplyAppsClient({ config, accessToken, fetcher }: SupplyAppsClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  return {
    listApps: (filter: SupplyAppsListFilter) =>
      api.request({
        path: `/api/apps?${serializeSupplyAppsListFilter(filter)}`,
        schema: supplyInventoryAppsPageSchema,
      }),
    countAppsByStatus: (filter: SupplyAppStatusCountsFilter) =>
      api.request({
        path: `/api/apps/count-by-status?${serializeSupplyAppStatusCountsFilter(filter)}`,
        schema: supplyAppsStatusCountsSchema,
      }),
    getApp: (id: string) =>
      api.request({
        path: `/api/apps/${encodeURIComponent(id)}`,
        schema: supplyInventoryAppEditSchema,
      }),
    updateApp: (id: string, payload: SupplyAppMutationPayload) =>
      api.request({
        path: `/api/apps/inventory/${encodeURIComponent(id)}`,
        schema: appMutationResponseSchema,
        init: jsonInit('PUT', payload),
      }),
    archiveApp: (id: string) =>
      api.request({
        path: `/api/publisher-apps/apps/${encodeURIComponent(id)}/archive`,
        schema: emptyMutationResponseSchema,
        init: jsonInit('PUT', {}),
      }),
    activateApp: (id: string) =>
      api.request({
        path: `/api/publisher-apps/apps/${encodeURIComponent(id)}/unarchive`,
        schema: emptyMutationResponseSchema,
        init: jsonInit('PUT', {}),
      }),
    importApps: (file: File) => {
      const body = new FormData();
      body.append('file', file, file.name);
      return api.request({
        path: '/api/apps/csv/import-inventory-apps',
        schema: importResponseSchema,
        init: { method: 'POST', body },
      });
    },
    exportApps: (filter: SupplyAppsListFilter) =>
      api.request({
        path: `/api/apps/csv/export?${serializeSupplyAppsExportFilter(filter)}`,
        schema: z.string(),
        responseType: ApiResponseType.Text,
      }),
    listAppRequests: (filter: AppRequestsListFilter) =>
      api.request({
        path: `/exchange-apps?${serializeAppRequestsListFilter(filter)}`,
        schema: appRequestsPageSchema,
      }),
    processAppRequests: (requests: AppRequest[]) =>
      api.request({
        path: '/exchange-apps/bulk',
        schema: z.array(appRequestSchema),
        init: jsonInit('PUT', requests),
      }),
    exportAppRequests: (filter: AppRequestsListFilter) =>
      api.request({
        path: `/exchange-apps/csv/export?${serializeAppRequestsListFilter(filter)}`,
        schema: z.string(),
      }),
    listPlatforms: () =>
      api.request({ path: '/api/apps/platforms', schema: supplyAppsReferenceListSchema }),
    listIabCategories: () =>
      api.request({ path: '/api/iab-categories', schema: supplyAppsReferenceListSchema }),
    searchPublishers: (name: string) =>
      api.request({
        path: `/api/publishers?${toSearchParams({ name, page: 0, size: 50, sortProperty: 'name', sortDirection: SupplyAppsSortDirection.Ascending })}`,
        schema: publisherLookupPageSchema,
      }),
    listPublishers: async () => {
      const publishers: Array<{ id: string; name: string }> = [];
      const size = 100;
      let page = 0;
      let totalElements = 0;
      do {
        const response = await api.request({
          path: `/api/publishers?searchTerm=&page=${String(page)}&size=${String(size)}&sortProperty=name&sortDirection=ASC`,
          schema: publisherLookupPageSchema,
        });
        publishers.push(...response.content);
        totalElements = response.totalElements;
        if (response.content.length === 0) break;
        page += 1;
      } while (publishers.length < totalElements);
      return publishers;
    },
    collectAppFromUrl: (publisherId: string, appStoreUrl: string) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/apps/urls-data`,
        schema: collectedAppDataResponseSchema,
        init: jsonInit('POST', [appStoreUrl]),
      }),
    createPublisherApp: (publisherId: string, payload: CreateAppFromRequestPayload) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/apps`,
        schema: createdPublisherAppSchema,
        init: jsonInit('POST', payload),
      }),
    collectPublisherAppsFromUrls: (publisherId: string, urls: string[]) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/apps/urls-data`,
        schema: publisherAppsImportResponseSchema,
        init: jsonInit('POST', urls),
      }),
    validatePublisherApps: (publisherId: string, apps: PublisherAppValidationPayload[]) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/apps/validate`,
        schema: publisherAppsImportResponseSchema,
        init: jsonInit('POST', apps),
      }),
    collectPublisherAppsFromCsv: (publisherId: string, file: File) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/apps/csv-collect`,
        schema: publisherAppsImportResponseSchema,
        init: fileUploadInit('POST', file),
      }),
    createPublisherApps: (publisherId: string, apps: PublisherAppImportPayload[]) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/apps/with-validation`,
        schema: publisherAppsImportResponseSchema,
        init: jsonInit('POST', apps),
      }),
    exportPublisherApps: (publisherId: string) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/apps/csv/export`,
        schema: z.string(),
        responseType: ApiResponseType.Text,
      }),
    reimportPublisherApps: (publisherId: string, file: File) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/apps/csv/reimport`,
        schema: publisherAppsImportResponseSchema,
        init: fileUploadInit('PUT', file),
      }),
    downloadPublisherAppsCsvTemplate: (publisherId: string) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/apps/download/apps-csv-template`,
        schema: z.string(),
        responseType: ApiResponseType.Text,
      }),
  };
}

function serializeSupplyAppsExportFilter(filter: SupplyAppsListFilter) {
  const params = new URLSearchParams(serializeSupplyAppsListFilter(filter));
  params.set('adswizzGenresIds', '');
  params.set('networkIds', '');
  params.set('ids', '');
  params.set('exchangeOnly', 'false');
  return params.toString();
}

function fileUploadInit(method: 'POST' | 'PUT', file: File) {
  const body = new FormData();
  body.append('file', file, file.name);
  return { method, body };
}
